import { useRef, useState } from "react"
import type { Dispatch, FC, ReactElement, ReactPortal, RefObject, SetStateAction } from "react"

import { createPortal } from "react-dom"
import { useCopyToClipboard, useIsomorphicLayoutEffect, useToggle } from "react-use"

import { CopyIcon } from "@/3514/assets"
import { Button, ETypographyColor, ETypographySize, ETypographyTag, Typography } from "@/3514/components"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import { copyButtonConfig as config } from "./copy-button.config"
import { ECopyButtonVariant as EVariant } from "./copy-button.types"

type TProps = { variant?: EVariant; valueToCopy: string; className?: string }

const classes = {
    container: (): string => "relative",
    tooltip: (): string =>
        buildCssClassName(
            "absolute",
            "left-[-30%]",
            "rounded-[3px]",
            "z-[1000]",
            "pointer-events-none",
            "px-[5px]",
            "py-[2px]",
            "bg-blue-200",
            "after:content-['']",
            "after:absolute",
            "after:-bottom-1",
            "after:left-1/2",
            "after:-translate-x-1/2",
            "after:border-x-[4px]",
            "after:border-x-transparent",
            "after:border-t-[4px]",
            "after:border-t-blue-200"
        )
}

const Component: FC<TProps> = ({
    variant = EVariant.Blue,
    valueToCopy = String(""),
    className = String("")
}: TProps): ReactElement => {
    const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const [, copyToClipboard]: ReturnType<typeof useCopyToClipboard> = useCopyToClipboard()

    const [isTooltipVisible, setIsTooltipVisible]: ReturnType<typeof useToggle> = useToggle(false)

    useIsomorphicLayoutEffect((): TEmptyCallback => {
        if (isTooltipVisible) {
            const timer: ReturnType<typeof setTimeout> = setTimeout((): void => setIsTooltipVisible(false), 1000)

            return (): void => clearTimeout(timer)
        }

        return emptyCallback
    }, [isTooltipVisible])

    const {
        isActionAffected,
        actions: { onMouseEnter, onMouseLeave, onTouchStart, onTouchEnd }
    }: TUseInteractionState = useInteractionState()

    const handleIconAction: ReturnType<typeof withPressHandler> = withPressHandler((): void => {
        copyToClipboard(valueToCopy)
        setIsTooltipVisible(true)
    })

    return (
        <div className={classes.container()} ref={ref}>
            {/* eslint-disable-next-line */}
            <_Tooltip targetRef={ref} isVisible={isTooltipVisible} />

            <Button
                isContainer
                aria-label="Copy to clipboard"
                className={buildCssClassName({
                    [config.buttonColorsMap[variant](isActionAffected)]: variant,
                    [className]: className
                })}
                onKeyDown={handleIconAction}
                onClick={handleIconAction}
                {...{ onTouchEnd, onTouchStart, onMouseEnter, onMouseLeave }}
            >
                <CopyIcon color={config.iconColorsMap[variant](isActionAffected)} />
            </Button>
        </div>
    )
}

type _TTooltipProps = { targetRef: RefObject<HTMLDivElement>; isVisible: boolean }

const _Tooltip: FC<_TTooltipProps> = ({ targetRef, isVisible }: _TTooltipProps): ReactPortal => {
    const [coords, setCoords]: [Partial<DOMRect>, Dispatch<SetStateAction<Partial<DOMRect>>>] = useState<
        Partial<DOMRect>
    >({ top: 0, left: 0 })

    const { t }: IUseTranslation = useTranslation()

    useIsomorphicLayoutEffect(
        (): void => (
            targetRef.current && isVisible
                ? setCoords({
                      top: targetRef.current.getBoundingClientRect().bottom + window.scrollY - 60,
                      left: targetRef.current.getBoundingClientRect().left + window.scrollX - 10
                  })
                : void 0,
            void 0
        ),
        [targetRef, isVisible]
    )

    return isVisible
        ? createPortal(
              <div className={classes.tooltip()} style={{ ...coords }}>
                  <Typography
                      text={t("common.copied")}
                      tag={ETypographyTag.Span}
                      color={ETypographyColor.White}
                      size={ETypographySize.Tiny}
                  />
              </div>,
              document.body
          )
        : null
}

Component.displayName = "CopyButton"

export { Component as CopyButton }
